import { DOCUMENT, Inject, Injectable, NgZone, OnDestroy } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import Map from 'ol/Map.js';
import View from 'ol/View';
import Zoom from 'ol/control/Zoom';
import { getCenter } from 'ol/extent';
import { defaults as defaultInteractions } from 'ol/interaction';
import TileLayer from 'ol/layer/Tile';
import Projection from 'ol/proj/Projection';
import XYZ from 'ol/source/XYZ';
import TileGrid from 'ol/tilegrid/TileGrid';

import { PreferencesService } from '../preferences/preferences-service/preferences.service';
import { WorkspaceService } from '../workspace/workspace.service';
import { PlanDataService, TPlanData } from './plan-data.service';

import { WorkspaceApiProviderService } from '@core/api';
import { WindowService } from '@core/services';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { EPlanPointMode } from '../../shared/enums/plan-point-mode.enum';
import { SiteService } from '../site/site.service';
import { TWorkspace } from '../workspace/workspace.model';
import { TPlanMap } from './plan-feature.model';
import { EPlanModule } from './plan-module.enum';

import { EIconPath } from '../../shared/enums/icons.enum';
import { logErrorInSentry } from '../errors/response-error';

type ImageData = {
  layer: TileLayer<XYZ>;
  view: View;
};

@Injectable({
  providedIn: 'root',
})
export class PlanService implements OnDestroy {
  public selectionChange$ = new Subject<EPlanPointMode>();
  public mapMoved$ = new Subject<void>();

  private readonly destroy$ = new Subject<void>();
  private presignedUrls: Record<string, string> = null;

  private planData: TPlanData = this.planDataService.getPlan();
  private extent: number[] = [];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private planDataService: PlanDataService,
    private workspaceService: WorkspaceService,
    private preferencesService: PreferencesService,
    private workspaceApiProviderService: WorkspaceApiProviderService,
    private siteService: SiteService,
    private windowService: WindowService,
    private ngZone: NgZone,
  ) {
    this.windowService.resize$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.siteService.siteViewChecked$.pipe(take(1))),
      )
      .subscribe(() => {
        this.ngZone.runOutsideAngular(() => {
          this.planData.instance.site?.updateSize();
        });
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  createPlan(
    {
      module = EPlanModule.SITE,
      element = null,
    }: {
      module?: EPlanModule;
      element?: HTMLElement;
    } = {},
    workspaceId: string = null,
  ): ImageData {
    const { layer, view } = this.getImageData(workspaceId);
    this.createMap({ layer, view, module, element });

    return { layer, view };
  }

  getImageData(_workspaceId: string): ImageData {
    let workspace: TWorkspace;

    if (!_workspaceId) {
      workspace = this.workspaceService.getActiveWorkspace();
    } else {
      workspace = this.workspaceService.getWorkspaces()[_workspaceId];
    }

    this.extent = workspace.sitePlan.extent;

    const layer = new TileLayer({
      source: this.createSourceFromPreSignedUrls(workspace),
    });

    const view = this.createView();

    return { layer, view };
  }

  fetchPresignedUrls(workspaceId: string): Observable<{ presignedUrls: Record<string, string> }> {
    return this.workspaceApiProviderService.getSitePlanPresignedUrls(workspaceId).pipe(
      tap(({ presignedUrls }) => {
        this.presignedUrls = presignedUrls;
      }),
    );
  }

  createSourceFromPreSignedUrls(_workspace: TWorkspace): XYZ {
    const signedUrlMap = this.presignedUrls;

    return new XYZ({
      projection: 'PIXELS',
      tileGrid: this.createTileGrid(_workspace),
      tileUrlFunction: (tileCoord: number[]): string | undefined => {
        const z = tileCoord[0];
        const x = tileCoord[1];
        const y = tileCoord[2];

        const key = `${z}/${x}/${y}.png`;
        const signedUrl = signedUrlMap?.[key];

        if (signedUrl) {
          return signedUrl;
        }

        logErrorInSentry({
          message: `Tile not found in pre-signed list for workspace ${_workspace.workspaceId}`,
          error: new Error(
            `Error when creating tiles with pre-signed URLs: tile ${key} not found in the list`,
          ),
        });
        return undefined;
      },
    });
  }

  createMap({
    layer,
    view,
    module,
    element,
  }: {
    layer: TileLayer<XYZ>;
    view: View;
    module: EPlanModule;
    element: HTMLElement;
  }): void {
    this.planData.instance[module] = new Map({
      target: element,
      layers: [layer],
      view,
      controls: [],
      interactions: defaultInteractions({
        pinchRotate: false,
      }),
    }) as TPlanMap;

    if (module === EPlanModule.SITE) {
      const zoomPanel = this.createZoom();

      this.planData.instance[module].controls.extend([zoomPanel]);
    }

    this.fitPlan(module);

    const planView = this.planData.instance[module].getView();

    this.planDataService.setPlan({
      element,
      resolution: planView.getResolution(),
      extent: planView.calculateExtent(this.planData.instance[module].getSize()),
      zoom: planView.getZoom(),
    });

    planView.setMaxZoom(24);
    planView.setMinZoom(this.planData.zoom);

    this.planData.instance[module].on('moveend', () => {
      const zoomOutButtons = this.document.getElementsByClassName('ol-zoom-out');
      const zoomInButtons = this.document.getElementsByClassName('ol-zoom-in');

      let zoomOut;
      let zoomIn;

      if (module === EPlanModule.POINT && zoomOutButtons.length > 1) {
        zoomOut = zoomOutButtons[1];
        zoomIn = zoomInButtons[1];
      } else {
        zoomOut = zoomOutButtons[0];
        zoomIn = zoomInButtons[0];
      }
    });

    this.planData.instance[module].on('movestart', () => {
      this.mapMoved$.next();
    });
  }

  createTileGrid(workspace: TWorkspace): TileGrid {
    return new TileGrid({
      extent: this.extent,
      minZoom: workspace.sitePlan.minZoom,
      resolutions: workspace.sitePlan.resolutions,
    });
  }

  createProjection(): Projection {
    return new Projection({
      code: 'xkcd-image',
      units: 'pixels',
      extent: this.extent,
    });
  }

  createView(): View {
    return new View({
      center: getCenter(this.extent),
      projection: this.createProjection(),
      zoomFactor: this.getZoomFactor(),
      constrainOnlyCenter: true,
      extent: this.extent,
    });
  }

  getZoom(module: EPlanModule): number {
    return this.planData.instance[module] ? this.planData.instance[module].getView().getZoom() : 1;
  }

  setZoom(zoom: number, module: EPlanModule): TPlanData {
    if (this.planData.instance[module]) {
      this.planData.instance[module].getView().setZoom(zoom);
    }

    return this.planData;
  }

  setCenter(center: number[], module: EPlanModule): TPlanData {
    if (this.planData.instance[module]) {
      this.planData.instance[module].getView().setCenter(center);
    }

    return this.planData;
  }

  createZoom(): Zoom {
    const zoomInButton = this.document.createElement('div');
    const zoomOutImage = this.document.createElement('img');
    const zoomInImage = this.document.createElement('img');

    zoomInImage.src = EIconPath.PLAN_ZOOM_IN;
    zoomInImage.className = 'ol-zoom-in-icon';

    zoomInButton.appendChild(zoomInImage);
    zoomInButton.className = 'ol-zoom-in-container';
    zoomInButton.setAttribute('data-m-target', 'Site plan zoom button');

    zoomOutImage.src = EIconPath.PLAN_ZOOM_OUT;
    zoomOutImage.className = 'ol-zoom-out-icon';

    const zoom = new Zoom({
      zoomInLabel: zoomInButton,
      zoomOutLabel: zoomOutImage,
    });

    return zoom;
  }

  toggleZoom(addZoom: boolean): void {
    if (addZoom) {
      const zoom = this.createZoom();

      this.planData.instance.point.controls.extend([zoom]);
    }
  }

  getZoomFactor(): number {
    const preferences = this.preferencesService.getPreferences();
    const zoomFactor = preferences.mapZoomFactor;

    return zoomFactor ? zoomFactor : 2.0;
  }

  fitPlan(module: EPlanModule): void {
    this.planData.instance[module].getView().fit(this.extent, {
      size: this.planData.instance[module].getSize(),
    });
  }

  fitSitePlan(): void {
    this.fitPlan(EPlanModule.SITE);
  }

  fitPointPlan(): void {
    this.fitPlan(EPlanModule.POINT);
  }

  getCenter(module: EPlanModule): number[] {
    return this.planData.instance[module]
      ? this.planData.instance[module].getView().getCenter()
      : null;
  }

  clearSitePlanCache(): void {
    this.presignedUrls = null;
    this.planDataService.clearPlan();
    this.planDataService.clearPointPlan();
  }
}
